import React, { useContext, useEffect, useRef, useState } from "react";
import { FileUpload } from "../../common/FileUpload/FileUpload";
import FormioSingleUpload from "../../common/FormioSingleUpload/FormioSingleUpload";
import ToggleButton from "../../common/ToggleButton/ToggleButton";

import { createRoot } from "react-dom/client";
import { HiSquare3Stack3D } from "react-icons/hi2";
import { BsFillFileEarmarkBinaryFill } from "react-icons/bs";
import { GET } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import { resolveTemplateSource } from "../../configs/templateTypes";
import AuthContext from "../../store/auth-context";
import { useParams, useLocation } from "react-router-dom";
import { LuImageOff } from "react-icons/lu";
import { RxReload } from "react-icons/rx";
import CfLoader from "../../common/CfLoader/CfLoader";
import { imgRegex } from "../../configs/App.Regex";
import CfBreadcrumb from "../../common/CfBreadcrumb/CfBreadcrumb";
import { hasFormDataAttributes } from "../../utils/formUtils/hasFormDataAttributes";
import { useTranslation } from "react-i18next";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import {
  injectTemplatePreview,
  updateTemplatePreview,
} from "../../common/HtmlDesigner/preview/templatePreview";

const CertificateIssue = () => {
  const [isUploadMethod, setIsUploadMethod] = useState(false);

  const {
    credential_template_id,
    issuer_credential_template_id,
    programme_code,
  } = useParams();
  const [currentSVG, setCurrentSVG] = useState("");
  const [previewTemplateType, setPreviewTemplateType] = useState();
  const [certificateData, setCertificateData] = useState({});
  const [fieldsChanged, setFieldsChanged] = useState({});
  const [savedTemplateValues, setSavedTemplateValues] = useState({});

  const location = useLocation();
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();

  const [breadcrumbItems, setBreadcrumbItems] = useState([]);

  const [transactionName, setTransactionName] = useState("");
  const [customFieldChanged, setCustomFieldChanged] = useState({});
  const [isToggleOn, setIsToggleOn] = useState(false);
  const [creditCount, setCreditCount] = useState(1);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [isPostPaid, setIsPostPaid] = useState(true);

  const [hasFormFields, setHasFormFields] = useState(false);

  const isMountedRef = useRef(true);

  const GET_CERTIFICATE_DATA = (credential_template_id) => {
    GET(
      `${EntityConstants.GET_CREDENTIALS_SUBJECT_FORM}${credential_template_id}/subject?type=form`,
    )
      .then((response) => response.json())
      .then(async (data) => {
        setCertificateData(data);
        try {
          const formComponentsResponse = await fetch(
            data.credential_subject_form,
          );
          const formComponents = await formComponentsResponse.json();

          const hasFields = hasFormDataAttributes(formComponents);
          setHasFormFields(hasFields);
        } catch (error) {
          console.error("Error fetching form components:", error);
          setHasFormFields(false);
        }
      })
      .catch((error) => {
        console.error("Error loading certificate subject form:", error);
        setHasFormFields(false);
      });
  };

  const GET_USER_DETAILS = async () => {
    const response = await GET(EntityConstants.CURRENT_USER);
    const data = await response.json();
    if (response.ok) {
      if (
        data.mode_of_payment === "pre_paid" &&
        data.available_credits !== undefined
      ) {
        setAvailableCredits(data.available_credits);
        setIsPostPaid(false);
      } else if (data.mode_of_payment === "post_paid") {
        setIsPostPaid(true);
      } else {
        console.log("Invalid mode_of_payment or missing available_credits.");
      }
    } else {
      console.log("Failed to fetch user details.");
    }
  };

  const renderToContainer = (container, content) => {
    if (!container) return;
    const root = createRoot(container);
    root.render(content);
  };

  const showPreviewNotFound = (container) =>
    renderToContainer(
      container,
      <div className="text-black flex justify-center w-full h-full text-lg">
        <div className="flex flex-col items-center gap-2 w-fit h-fit">
          <span className="flex items-center gap-2 w-fit h-fit  bg-gray-100 px-2 py-1 rounded-lg">
            <p>{t("designs:could_not_find_image")}</p>
            <LuImageOff className="text-cfPrimaryCardHover " />
          </span>
          <span>
            <p>{t("designs:create_new_template")}</p>
          </span>
        </div>
      </div>,
    );

  const showPreviewLoadFailed = (container) =>
    renderToContainer(
      container,
      <div className="text-black flex justify-center w-full h-full text-lg">
        <button
          type="button"
          className="flex items-center gap-2 w-fit h-fit rounded-lg cursor-pointer focus:outline-none"
          onClick={() => {
            const el = document.getElementById("certificate_container");
            renderToContainer(
              el,
              <span className="w-full flex justify-center">
                <CfLoader />
              </span>,
            );
            fetchDefaultSVG();
          }}
        >
          <p>{t("designs:could_not_load_image")}</p>
          <RxReload className="text-cfPrimaryCardHover " />
        </button>
      </div>,
    );

  const fetchDefaultSVG = async () => {
    try {
      const metaResponse = await GET(
        `${EntityConstants.GET_USER_PREVIEW_SVG}?issuer_credential_template_id=${issuer_credential_template_id}`,
      );
      if (!metaResponse.ok) {
        showPreviewLoadFailed(document.getElementById("certificate_container"));
        return;
      }
      const meta = await metaResponse.json();
      if (!isMountedRef.current) return;

      setTransactionName(meta.certificate_name);
      const { url, templateType } = resolveTemplateSource(meta);
      if (!url) {
        showPreviewNotFound(document.getElementById("certificate_container"));
        return;
      }
      if (!meta.template_html && url === meta.html_url) {
        const saved = { ...meta.template_variables_data?.data };
        delete saved.submit;
        setSavedTemplateValues(saved);
      }

      const response = await fetch(url);
      const svgText = response.ok ? await response.text() : "";
      if (!isMountedRef.current) return;

      const container = document.getElementById("certificate_container");

      if (response.ok) {
        setPreviewTemplateType(templateType);
        setCurrentSVG(svgText);
        if (!container) return;
        container.style.filter = "drop-shadow(0 0 2px rgba(0, 0, 0, 0.264))";
        injectTemplatePreview(container, svgText, templateType);
      } else if (response.status === 404) {
        showPreviewNotFound(container);
      } else {
        showPreviewLoadFailed(container);
      }
    } catch (error) {
      console.error("Error loading SVG:", error);
      if (isMountedRef.current) {
        showPreviewLoadFailed(document.getElementById("certificate_container"));
      }
    }
  };

  const formFieldsChanged = (fields) => {
    if (
      fields.changed?.component?.key !== undefined &&
      fields.changed?.value !== undefined
    ) {
      if (
        fields.changed?.value[0]?.type &&
        imgRegex.test(fields.changed?.value[0].url)
      ) {
        isToggleOn
          ? setFieldsChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value[0].url,
            }))
          : setCustomFieldChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value[0].url,
            }));
      } else {
        isToggleOn
          ? setFieldsChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value,
            }))
          : setCustomFieldChanged((prevData) => ({
              ...prevData,
              // Add your new key-value pair here
              [fields.changed?.component?.key]: fields.changed?.value,
            }));
      }
    } else if (fields?.data) {
      setFieldsChanged(fields.data);
    }
  };

  const toggleUploadMethod = (bool) => {
    setIsUploadMethod(bool);
    if (!bool) {
      authContext.setRowCount(0);
      authContext.setFileUploaded(false);
      setCreditCount(1);
    } else {
      setCreditCount(authContext.rowCount);
    }
  };

  useEffect(() => {
    if (isUploadMethod) {
      const newCreditCount = authContext.rowCount || 0;
      if (availableCredits >= newCreditCount) {
        setCreditCount(newCreditCount);
      } else {
        setCreditCount("Insufficient Credits");
      }
    } else {
      const newCreditCount = 1;
      if (availableCredits >= newCreditCount) {
        setCreditCount(newCreditCount);
      } else {
        setCreditCount("Insufficient Credits");
      }
    }
  }, [authContext.rowCount, isUploadMethod, availableCredits]);

  useEffect(() => {
    if (currentSVG) {
      if (fieldsChanged) {
        const updatedFields = {
          ...savedTemplateValues,
          ...(isToggleOn ? fieldsChanged : customFieldChanged),
        };
        const previewEl = document.getElementById("certificate_container");
        updateTemplatePreview(
          previewEl,
          currentSVG,
          updatedFields,
          previewTemplateType,
        );
      }
    }
  }, [
    fieldsChanged,
    customFieldChanged,
    savedTemplateValues,
    currentSVG,
    previewTemplateType,
    isToggleOn,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    GET_USER_DETAILS();

    GET_CERTIFICATE_DATA(credential_template_id);
    fetchDefaultSVG();
    const previousPath = authContext.lastVisitedPath;
    if (
      previousPath === "/issuer/drafts" ||
      sessionStorage.getItem("previousPath") === "/issuer/drafts"
    ) {
      setBreadcrumbItems([
        {
          titleKey: "designs:receiver_details",
          to: `/issuer/certificate/issue/${credential_template_id}/${issuer_credential_template_id}/${programme_code}`,
        },
      ]);
    } else {
      setBreadcrumbItems([
        {
          titleKey: "designs:edit",
          to: `/certificate/edit/${credential_template_id}`,
        },
        {
          titleKey: "designs:receiver_details",
          to: `/issuer/certificate/issue/${credential_template_id}/${issuer_credential_template_id}/${programme_code}`,
        },
      ]);
    }
    authContext.lastVisitedPathHandler(location.pathname);
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <div className="mt-20" id={pageRootTestIds.certificateIssue}>
      {breadcrumbItems.length > 0 && (
        <CfBreadcrumb
          breadcrumbItems={breadcrumbItems}
          isToggleOn={isToggleOn}
          setIsToggleOn={setIsToggleOn}
          creditCount={creditCount}
          isPostPaid={isPostPaid}
          hasFormFields={hasFormFields}
          testId="certificate-issue-breadcrumb"
        />
      )}
      <div className="flex flex-col md:flex-row w-full">
        <div className="w-full md:w-[30%] md:shrink-0 max-h-none md:max-h-[84vh] md:min-h-[84vh] overflow-y-auto flex flex-col items-center bg-cfSecondary min-h-full">
          <div className="h-auto w-full sticky top-0 z-10 bg-cfSecondary shrink-0">
            <ToggleButton
              First_Text={t("designs:single_issuance")}
              Second_Text={t("designs:batch_issuance")}
              showText={true}
              LeftIcon={BsFillFileEarmarkBinaryFill}
              RightIcon={HiSquare3Stack3D}
              onToggle={toggleUploadMethod}
              UploadMethodBool={isUploadMethod}
            />
          </div>
          <div className="px-4 bg-cfSecondary h-full w-full">
            {isUploadMethod
              ? transactionName && (
                  <FileUpload
                    transaction_name={transactionName}
                    creditCount={creditCount}
                    isPostPaid={isPostPaid}
                  />
                )
              : transactionName && (
                  <FormioSingleUpload
                    toggleUploadMethod={toggleUploadMethod}
                    URL={certificateData?.credential_subject_form}
                    formFieldsChanged={formFieldsChanged}
                    transaction_name={transactionName}
                    isToggleOn={isToggleOn}
                    creditCount={creditCount}
                    isPostPaid={isPostPaid}
                  />
                )}
          </div>
        </div>

        <div className="w-full min-w-0 flex-1 p-3">
          <div
            className="flex flex-col flex-shrink max-h-[73vh]"
            id="certificate_container"
          >
            <CfLoader />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateIssue;
